<template>
  <div class="app-file-root">
    <label v-if="label" class="app-file-label" :for="inputId">{{ label }}</label>
    
    <div
      class="app-file-container"
      :class="containerClasses"
      @click="triggerFileInput"
      @dragover.prevent="onDragOver"
      @dragleave="onDragLeave"
      @drop.prevent="onDrop"
    >
      <input
        ref="fileInput"
        :id="inputId"
        type="file"
        class="app-file-hidden-input"
        :accept="accept"
        :multiple="multiple"
        :disabled="disabled"
        @change="onFileChange"
      />
      
      <div class="app-file-preview-area" v-if="previewUrl && isImage">
        <img :src="previewUrl" class="app-file-preview-img" alt="Vista previa" />
        <div class="app-file-overlay">
          <span class="material-icons app-file-overlay-icon">cached</span>
          <span class="app-file-overlay-text">Cambiar imagen</span>
        </div>
      </div>
      
      <div v-else class="app-file-placeholder-area">
        <span class="material-icons app-file-upload-icon">{{ icon || 'cloud_upload' }}</span>
        <span class="app-file-main-text">{{ selectedFileName || placeholder || 'Haz clic o arrastra un archivo aquí' }}</span>
        <span v-if="selectedFileName" class="app-file-sub-text">Hacer clic para cambiar</span>
      </div>

      <span
        v-if="clearable && (currentValue || selectedFileName)"
        class="material-icons app-file-clear-btn"
        @click.stop="clearValue"
      >close</span>
    </div>
    
    <small v-if="errorMessage" class="app-file-error app-file-error--visible">{{ errorMessage }}</small>
  </div>
</template>

<script>
export default {
  name: 'AppInputFile',
  props: {
    modelValue: { type: [File, FileList, String], default: null },
    value: { type: [File, FileList, String], default: null },
    label: { type: String, default: '' },
    placeholder: { type: String, default: 'Seleccionar archivo o arrastrarlo aquí...' },
    accept: { type: String, default: '*' },
    multiple: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    clearable: { type: Boolean, default: true },
    icon: { type: String, default: '' },
    state: { type: Boolean, default: null },
    errorMessage: { type: String, default: '' }
  },
  emits: ['update:modelValue', 'input', 'change'],
  data() {
    return {
      dragOver: false,
      selectedFileName: '',
      previewUrl: ''
    }
  },
  computed: {
    currentValue() {
      return this.modelValue !== undefined ? this.modelValue : this.value
    },
    isImage() {
      if (this.previewUrl) return true
      if (typeof this.currentValue === 'string' && (this.currentValue.startsWith('data:image/') || this.currentValue.match(/\.(jpeg|jpg|gif|png|webp|svg)/i))) {
        return true
      }
      return false
    },
    containerClasses() {
      return {
        'app-file-container--dragover': this.dragOver,
        'app-file-container--disabled': this.disabled,
        'app-file-container--valid': this.state === true,
        'app-file-container--invalid': this.state === false || !!this.errorMessage,
        'app-file-container--has-preview': this.previewUrl && this.isImage
      }
    },
    inputId() {
      return 'app-file-' + Math.random().toString(36).substr(2, 9)
    }
  },
  watch: {
    currentValue: {
      immediate: true,
      handler(val) {
        if (!val) {
          this.selectedFileName = ''
          this.previewUrl = ''
        } else if (val instanceof File) {
          this.selectedFileName = val.name
          if (val.type.startsWith('image/')) {
            this.previewUrl = URL.createObjectURL(val)
          } else {
            this.previewUrl = ''
          }
        } else if (val instanceof FileList && val.length > 0) {
          this.selectedFileName = Array.from(val).map(f => f.name).join(', ')
          if (val[0].type.startsWith('image/')) {
            this.previewUrl = URL.createObjectURL(val[0])
          } else {
            this.previewUrl = ''
          }
        } else if (typeof val === 'string') {
          this.selectedFileName = val.substring(val.lastIndexOf('/') + 1) || 'Archivo'
          this.previewUrl = val
        }
      }
    }
  },
  methods: {
    triggerFileInput() {
      if (this.disabled) return
      this.$refs.fileInput.click()
    },
    onFileChange(e) {
      const files = e.target.files
      if (!files || files.length === 0) return
      const result = this.multiple ? files : files[0]
      this.$emit('input', result)
      this.$emit('update:modelValue', result)
      this.$emit('change', e)
    },
    onDragOver() {
      if (this.disabled) return
      this.dragOver = true
    },
    onDragLeave() {
      this.dragOver = false
    },
    onDrop(e) {
      if (this.disabled) return
      this.dragOver = false
      const files = e.dataTransfer.files
      if (files && files.length > 0) {
        const result = this.multiple ? files : files[0]
        this.$emit('input', result)
        this.$emit('update:modelValue', result)
        this.$emit('change', { target: { files } })
      }
    },
    clearValue() {
      if (this.disabled) return
      this.$refs.fileInput.value = ''
      this.$emit('input', null)
      this.$emit('update:modelValue', null)
      this.selectedFileName = ''
      this.previewUrl = ''
    }
  }
}
</script>

<style scoped>
.app-file-root {
  width: 100%;
}

.app-file-label {
  display: block;
  margin-bottom: 4px;
  font-size: 13px;
  font-weight: 600;
  color: #939393;
}

.app-file-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 120px;
  padding: 1.25rem;
  background-color: #fff;
  border: 2px dashed #ced4da;
  border-radius: 0.375rem;
  cursor: pointer;
  position: relative;
  transition: all 0.25s ease;
  user-select: none;
}

.app-file-container:hover {
  border-color: #80bdff;
  background-color: rgba(0, 123, 255, 0.015);
}

.app-file-container--dragover {
  border-color: #007bff;
  background-color: rgba(0, 123, 255, 0.06);
}

.app-file-container--disabled {
  background-color: #e9ecef;
  border-color: #ced4da;
  cursor: not-allowed;
  opacity: 0.65;
}

.app-file-container--valid {
  border-color: #34C38F;
}

.app-file-container--invalid {
  border-color: #F46A6A;
}

.app-file-hidden-input {
  display: none;
}

.app-file-placeholder-area {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  pointer-events: none;
}

.app-file-upload-icon {
  font-size: 2.5rem;
  color: #ced4da;
  margin-bottom: 8px;
  transition: transform 0.2s ease, color 0.2s ease;
}

.app-file-container:hover .app-file-upload-icon {
  transform: translateY(-2px);
  color: #007bff;
}

.app-file-main-text {
  font-size: 0.9rem;
  font-weight: 500;
  color: #495057;
  word-break: break-all;
  max-width: 100%;
}

.app-file-sub-text {
  font-size: 0.75rem;
  color: #6c757d;
  margin-top: 4px;
}

.app-file-preview-area {
  width: 100%;
  max-height: 200px;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  overflow: hidden;
  border-radius: 0.25rem;
}

.app-file-preview-img {
  max-width: 100%;
  max-height: 180px;
  object-fit: contain;
  border-radius: 0.25rem;
}

.app-file-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  color: white;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.app-file-preview-area:hover .app-file-overlay {
  opacity: 1;
}

.app-file-overlay-icon {
  font-size: 2rem;
  margin-bottom: 4px;
}

.app-file-overlay-text {
  font-size: 0.85rem;
  font-weight: 500;
}

.app-file-clear-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  padding: 4px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.05);
  color: #495057;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.app-file-clear-btn:hover {
  background: #F46A6A;
  color: white;
}

.app-file-error {
  display: none;
  margin-top: 4px;
  font-size: 80%;
  color: #F46A6A;
}

.app-file-error--visible {
  display: block;
}
</style>
